import { useMemo } from 'react';
import { Node } from 'reactflow';
import {
  CanvasElementState,
  CanvasZIndex,
  resolveAmbientState,
} from '@components/MainStage/canvasStates';
import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import useStore, { RFState } from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  InitiativesSubLayerTypes,
  LogicalSubLayerTypes,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { matchesModelFiltersDto } from '@context/ProjectFilters/matchesModelFilters';
import { questionnaireViewFilters } from '@context/ProjectFilters/questionnaireViewFilters';
import { NodeDTOTypes } from '@store/services/nodes/types';
import { useModelFilters } from '@store/zustand/model-filters';
import { useQuestionnaireResult } from '@store/zustand/questionnaire-result';
import { ignoresModelFilters } from '@utils/filterAvailability';
import { Tabs } from '@views/Insights/types';
import { shallow } from 'zustand/shallow';

const canvasGraphSelector = (state: RFState) => ({
  nodes: state.nodes,
  edges: state.edges,
  phantoms: state.phantoms,
});

const FADE_ON_OPEN_TABS = new Set<Tabs>([
  Tabs.Default,
  Tabs.Completeness,
  Tabs.ModelEvolution,
  Tabs.CIA,
]);

const ANCESTOR_SUB_LAYERS: SubLayerTypes[] = [
  ContextualSubLayerTypes.StrategicEnablers,
  ContextualSubLayerTypes.StrategicPriorities,
  ConceptualSubLayerTypes.BusinessAttributes,
  ConceptualSubLayerTypes.SecurityAttributes,
];

const hideByFilters = (node: Node): Node => ({
  ...node,
  hidden: true,
  data: {
    ...node.data,
    canvas: { ...node.data.canvas, hiddenByFilters: true },
  },
});

/**
 * The canvas as the user sees it: the store graph with collapse, the model
 * filters, the questionnaire-result view, focus dimming, ambient state and
 * edge hiding applied, in that order.
 *
 * An insight computed over the whole model (`ignoresModelFilters`, e.g. Key
 * controls) skips the model filters, so every entity it can highlight stays
 * on the canvas.
 *
 * Filtering is one call of the shared predicate per entity node, so AND
 * across categories and OR within one hold here exactly as they do in the
 * list, the export and the drawer guard. Layer and sub-layer chrome nodes
 * carry no DTO to judge and are never filtered. After the reset at the top,
 * `hidden` and `canvas.hiddenByFilters` are only ever set, never cleared, so
 * no step can resurrect what an earlier one hid.
 *
 * Three rules sit outside the predicate because they are not category
 * semantics and its other consumers must not inherit them:
 *
 * - The questionnaire result (View Controls) mirrors the report, so each
 *   entity is judged against the selection `questionnaireViewFilters` gives
 *   it, and a CIA-layer entity that is unreported or incomplete is hidden.
 * - In that same view a Capability, Function or Technology whose parents are
 *   all hidden is hidden with them, one sub-layer at a time from the top so a
 *   hidden Capability hides its Functions in turn.
 * - Still in that view, a Contextual or Conceptual entity stays only when it
 *   is a complete ancestor of a visible Service, reached through complete,
 *   visible or collapsed nodes, as the report lists the ancestors of the
 *   controls it includes. A Change Initiative is resolved last against the
 *   final visible set. Both rules only hide, so an ancestor the predicate
 *   already hid stays hidden whatever it is connected to.
 */
const useFilteredCanvas = ({
  applyCollapse = true,
}: { applyCollapse?: boolean } = {}) => {
  const { edges, nodes, phantoms } = useStore(canvasGraphSelector, shallow);
  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();
  const { insightsTab, isInsightsMode } = useProject();
  const { result: questionnaireResult } = useQuestionnaireResult();
  const filters = useModelFilters();
  const isUnfiltered = ignoresModelFilters({ isInsightsMode, insightsTab });

  return useMemo(() => {
    const filteredCanvas = { nodes, edges };

    const isTabFocusedOnOrModelEvolution = [
      Tabs.FocusOn,
      Tabs.ModelEvolution,
    ].includes(insightsTab);

    filteredCanvas.nodes = filteredCanvas.nodes.map((node) =>
      node.data.canvas?.hiddenByFilters
        ? {
            ...node,
            data: {
              ...node.data,
              canvas: {
                ...node.data.canvas,
                hiddenByFilters: false,
              },
            },
          }
        : node,
    );

    if (!isTabFocusedOnOrModelEvolution) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        const hidden = applyCollapse
          ? node.data.canvas?.collapsed ?? false
          : false;

        return Boolean(node.hidden) === hidden ? node : { ...node, hidden };
      });
    } else if (!applyCollapse) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) =>
        node.data.canvas?.collapsed ? { ...node, hidden: false } : node,
      );
    }

    const hasQuestionnaireResult = Boolean(questionnaireResult);

    filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
      if (isUnfiltered || node.type !== NodesTypes.CustomNode) return node;

      const dto = node.data?.dto;
      const subLayer = node.parentNode as SubLayerTypes;

      const isMatch = matchesModelFiltersDto(
        dto,
        subLayer,
        questionnaireViewFilters(
          filters,
          dto,
          subLayer,
          hasQuestionnaireResult,
        ),
      );

      return isMatch ? node : hideByFilters(node);
    });

    if (questionnaireResult) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;
        if (!CiaSubLayers.includes(node.parentNode as any)) return node;
        if (node.data.dto.is_reported === false || !node.data.dto.is_complete) {
          return hideByFilters(node);
        }
        return node;
      });

      const orphanCascade = [
        NodeDTOTypes.Capabilities,
        NodeDTOTypes.Functions,
        NodeDTOTypes.Technologies,
      ];

      orphanCascade.forEach((orphanType) => {
        const visibleIds = new Set(
          filteredCanvas.nodes.filter((n) => !n.hidden).map((n) => n.id),
        );
        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (node.hidden || node.type !== NodesTypes.CustomNode) return node;
          if (node.data?.dto?.type !== orphanType) return node;
          const parentsIds: string[] = node.data?.dto?.parentsIds ?? [];
          const hasVisibleParent = parentsIds.some((id) => visibleIds.has(id));
          return hasVisibleParent ? node : hideByFilters(node);
        });
      });

      const nodeById = new Map(
        filteredCanvas.nodes.map((node) => [node.id, node]),
      );
      const connectedAncestorIds = new Set<string>();

      const collectAncestors = (id: string) => {
        const ancestor = nodeById.get(id);

        if (!ancestor || connectedAncestorIds.has(id)) return;

        const isReachable =
          Boolean(ancestor.data?.dto?.is_complete) &&
          (!ancestor.hidden || Boolean(ancestor.data?.canvas?.collapsed));

        if (!isReachable) return;

        connectedAncestorIds.add(id);
        ancestor.data?.dto?.parentsIds?.forEach(collectAncestors);
      };

      filteredCanvas.nodes.forEach((node) => {
        if (
          node.type !== NodesTypes.CustomNode ||
          node.hidden ||
          node.parentNode !== LogicalSubLayerTypes.Services
        )
          return;

        node.data?.dto?.parentsIds?.forEach(collectAncestors);
      });

      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;
        if (!ANCESTOR_SUB_LAYERS.includes(node.parentNode as SubLayerTypes)) {
          return node;
        }
        if (node.data?.canvas?.hiddenByFilters) return node;

        return connectedAncestorIds.has(node.id) ? node : hideByFilters(node);
      });

      const visibleIds = new Set(
        filteredCanvas.nodes.filter((n) => !n.hidden).map((n) => n.id),
      );

      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;
        if (node.parentNode !== InitiativesSubLayerTypes.ChangeInitiatives) {
          return node;
        }

        const parentsIds: string[] = node.data?.dto?.parentsIds ?? [];
        const hasVisibleParent = parentsIds.some((id) => visibleIds.has(id));
        const isReported = node.data.dto.is_reported !== false;
        const isVisible =
          node.data.dto.is_complete && isReported && hasVisibleParent;

        return isVisible ? node : hideByFilters(node);
      });
    }

    if (isFocusActive) {
      const focusedSet = new Set(focusedIds);

      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const outsideFocus = !focusedSet.has(node.id);

        return {
          ...node,
          ...(outsideFocus
            ? { selectable: false, focusable: false, draggable: false }
            : {}),
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              disabled: node.data.canvas?.disabled || outsideFocus,
              preventSelection:
                node.data.canvas?.preventSelection || outsideFocus,
            },
          },
        };
      });
    }

    const openNode = filteredCanvas.nodes.find(
      (node) => node.selected && node.type === NodesTypes.CustomNode,
    );
    const openEdge = filteredCanvas.edges.find((edge) => edge.selected);
    const isOpen =
      !isFocusActive &&
      FADE_ON_OPEN_TABS.has(insightsTab) &&
      Boolean(openNode || openEdge);

    const activeIds = new Set<string>();
    if (openNode) activeIds.add(openNode.id);
    if (openEdge) {
      activeIds.add(openEdge.source);
      activeIds.add(openEdge.target);
    }

    const openNodeId = openNode?.id ?? null;
    const relatedIds = new Set<string>();

    if (openNodeId && isOpen) {
      filteredCanvas.edges.concat(phantoms).forEach((edge) => {
        if (edge.source === openNodeId) relatedIds.add(edge.target);
        else if (edge.target === openNodeId) relatedIds.add(edge.source);
      });
    }

    filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
      if (node.type !== NodesTypes.CustomNode) return node;

      const state = resolveAmbientState({
        isDisabled: Boolean(node.data.canvas?.disabled),
        isFaded: Boolean(node.data.canvas?.faded),
        isOpen,
        isActive: activeIds.has(node.id),
        isRelated: relatedIds.has(node.id),
      });

      const zIndex =
        state === CanvasElementState.Disabled
          ? CanvasZIndex.DisabledEntity
          : CanvasZIndex.Entity;

      if (node.data.canvas?.state === state && node.zIndex === zIndex) {
        return node;
      }

      return {
        ...node,
        zIndex,
        data: {
          ...node.data,
          canvas: { ...node.data.canvas, state },
        },
      };
    });

    const hiddenNodeIds = new Set(
      filteredCanvas.nodes.filter((node) => node.hidden).map((node) => node.id),
    );

    const disabledNodeIds = new Set(
      filteredCanvas.nodes
        .filter(
          (node) => node.data?.canvas?.state === CanvasElementState.Disabled,
        )
        .map((node) => node.id),
    );

    const fadedNodeIds = new Set(
      filteredCanvas.nodes
        .filter((node) => node.data?.canvas?.state === CanvasElementState.Faded)
        .map((node) => node.id),
    );

    filteredCanvas.edges = filteredCanvas.edges.concat(phantoms).map((edge) => {
      const isDisabled =
        Boolean(edge.data?.isPhantom) ||
        disabledNodeIds.has(edge.source) ||
        disabledNodeIds.has(edge.target);

      const isFaded =
        fadedNodeIds.has(edge.source) || fadedNodeIds.has(edge.target);

      const hidden =
        hiddenNodeIds.has(edge.source) || hiddenNodeIds.has(edge.target);

      const state = resolveAmbientState({
        isDisabled,
        isFaded,
        isOpen,
        isActive: edge.id === openEdge?.id,
        isRelated: edge.source === openNodeId || edge.target === openNodeId,
      });

      if (
        Boolean(edge.hidden) === hidden &&
        edge.data?.canvas?.state === state
      ) {
        return edge;
      }

      return {
        ...edge,
        hidden,
        data: {
          ...edge.data,
          canvas: { ...edge.data?.canvas, state },
        },
      };
    });

    return filteredCanvas;
  }, [
    nodes,
    edges,
    phantoms,
    insightsTab,
    filters,
    isUnfiltered,
    questionnaireResult,
    isFocusActive,
    focusedIds,
    applyCollapse,
  ]);
};

export default useFilteredCanvas;
